/**
 * useIdleLogout — Auto-logout after inactivity
 *
 * Tracks user activity (mouse, keyboard, touch, scroll) and
 * triggers logout after the configured idle timeout.
 * Only active when user is authenticated.
 */

import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "./useAuth";

/** Default idle timeout: 30 minutes */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

/** Events that count as user activity */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "pointerdown",
];

export function useIdleLogout(timeoutMs = IDLE_TIMEOUT_MS) {
  const { isAuthenticated, logout } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutCalledRef = useRef(false);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    logoutCalledRef.current = false;
    timerRef.current = setTimeout(() => {
      if (!logoutCalledRef.current) {
        logoutCalledRef.current = true;
        console.info("[IdleLogout] User idle for %dmin — logging out", timeoutMs / 60000);
        void logout().then(() => {
          // Signal to LoginPage why the user was logged out
          const url = new URL(window.location.href);
          url.searchParams.set("reason", "idle");
          window.history.replaceState({}, "", url.toString());
          // Dispatch event so LoginPage can pick it up immediately
          window.dispatchEvent(new CustomEvent("idle-logout"));
        });
      }
    }, timeoutMs);
  }, [timeoutMs, logout]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Start timer immediately
    resetTimer();

    // Throttle activity events to avoid excessive timer resets
    let lastReset = Date.now();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastReset > 5000) {
        // Only reset every 5s at most
        lastReset = now;
        resetTimer();
      }
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    // Also reset on visibility change (tab refocus)
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        resetTimer();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAuthenticated, resetTimer]);
}
